/**
 * CustomCreatePage — user enters a word to create a custom game link.
 */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createCustomGame, setGameCode, setMessage, clearMessage } from '../store/slices/gameSlice';
import { toTurkishUpper, isTurkishLetter } from '../utils/gameLogic';
import { WORD_LENGTH } from '../utils/constants';

const CustomCreatePage = () => {
  const dispatch = useDispatch();
  const gameCode = useSelector((state) => state.game.gameCode);
  const dictLoaded = useSelector((state) => state.dictionary.loaded);
  const message = useSelector((state) => state.game.message);

  const [word, setWord] = useState('');
  const [copied, setCopied] = useState(false);

  // Reset gameCode on mount
  useEffect(() => {
    dispatch(setGameCode(''));
    return () => {
      dispatch(clearMessage());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    // Filter to only Turkish letters
    const filtered = [...val]
      .filter((ch) => isTurkishLetter(ch))
      .join('');
    const upper = toTurkishUpper(filtered);
    if ([...upper].length <= WORD_LENGTH) {
      setWord(upper);
    }
  };

  const handleCreate = () => {
    if ([...word].length !== WORD_LENGTH) {
      dispatch(setMessage('Kelime 5 harf olmalıdır.'));
      setTimeout(() => dispatch(clearMessage()), 2000);
      return;
    }
    dispatch(createCustomGame({ word }));
  };

  const gameUrl = gameCode
    ? `${window.location.origin}/play/${gameCode}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(gameUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = gameUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!dictLoaded) {
    return (
      <div className="flex items-center justify-center flex-1">
        <p className="text-lg text-gray-500 dark:text-gray-400 animate-pulse">
          Sözlük yükleniyor...
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4 py-6 max-w-sm mx-auto w-full">
      <div className="text-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold mb-3 flex items-center justify-center gap-2">
          <span className="text-gray-400">🔗</span>
          <span>Özel Kelime</span>
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
          Arkadaşlarınıza meydan okuyun! 5 harfli bir Türkçe kelime girin ve
          paylaşılabilir bir bağlantı oluşturun.
        </p>
      </div>

      {/* Input Group */}
      <div className="w-full mb-5">
        <div className="relative group">
          <input
            type="text"
            value={word}
            onChange={handleInputChange}
            placeholder="KELİME GİRİN"
            maxLength={ WORD_LENGTH }
            className="w-full text-center text-2xl font-extrabold tracking-[0.15em] uppercase px-4 py-4 border-2 border-gray-300 dark:border-gray-700 rounded-xl bg-white/50 dark:bg-black/20 backdrop-blur-sm focus:outline-none focus:border-correct dark:focus:border-correct focus:ring-4 focus:ring-correct/20 transition-all placeholder:text-gray-300 dark:placeholder:text-gray-800"
            autoFocus
          />
          {word.length > 0 && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-mono text-gray-400">
              {word.length}/5
            </div>
          )}
        </div>
        
        {message && (
          <p className="text-red-500 font-bold mt-2 text-xs text-center animate-bounce">
            {message}
          </p>
        )}
      </div>

      {/* Action Area */}
      {!gameCode ? (
        <button
          onClick={handleCreate}
          className={`
            w-full py-3 rounded-xl font-bold text-lg transition-all shadow-lg
            ${word.length === WORD_LENGTH 
              ? 'bg-correct text-white hover:opacity-90 scale-100' 
              : 'bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed scale-[0.98]'}
          `}
          disabled={word.length !== WORD_LENGTH}
        >
          Bağlantı Oluştur
        </button>
      ) : (
        <div className="w-full space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="p-4 bg-correct/10 border-2 border-correct rounded-2xl">
            <p className="text-sm text-correct font-bold mb-1">Bağlantınız Hazır!</p>
            <p className="text-xs break-all opacity-70 mb-3">{gameUrl}</p>
            <button
              onClick={handleCopy}
              className="w-full py-3 rounded-xl bg-correct text-white font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2"
            >
              {copied ? '✓ Kopyalandı' : 'Bağlantıyı Kopyala 📋'}
            </button>
          </div>
          <button
            onClick={() => {
              dispatch(setGameCode(''));
              setWord('');
            }}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            Yeni Bir Tane Oluştur
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomCreatePage;
